import { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardBody, Dropdown, Row, Col } from "react-bootstrap";
import moment from "moment";
import PostCalendar from "./PostCalendar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRecycle, faClone, faFilter } from "@fortawesome/free-solid-svg-icons";
import { faLinkedin, faYoutube, faMeta, faTiktok } from "@fortawesome/free-brands-svg-icons";
import { useFormik } from "formik";
import crudApi from "@serie3/common/api/crudApi";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import { canAccessAllPlatforms } from "@serie3/common/config/RoleRights";
import { formatDate } from "@serie3/common/Shared/timeingStuff";
import PostFormView from "./components/PostFormView";
import NewModal from "../shared/Modals/NewModal";
import EllipsisVerticalToggle from "../shared/Dropdown/EllipsisVerticalToggle";
import DateTimeField from "../Form/DateTimeField";
import Timezones from "../Form/Timezones";
import SaveButton from "../shared/Buttons/SaveButton";
import Interactions from "../shared/Interactions";
import YoutubeIcon from "../shared/YoutubeIcon";

// ── platform selector ─────────────────────────────────────────────────────────

const ALL_PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", icon: faLinkedin, color: "#0077b5" },
  { key: "youtube",  label: "YouTube",  icon: faYoutube,  color: "#ff0000" },
  { key: "meta",     label: "Meta",     icon: faMeta,     color: "#0081fb" },
  { key: "tiktok",   label: "TikTok",   icon: faTiktok,   color: "#010101" },
];

// post types the recycle endpoint understands (media format)
const POST_TYPES = ["Text", "TextImage", "Carousel", "Video", "Shortform", "Stories", "Poll", "LinkPost"];

// content types the recycle endpoint understands (editorial angle, set by the AI generator)
const CONTENT_TYPES = ["story", "educational", "opinion", "list"];

// age presets — value is `days` query param; 0 means "ignore time"
const AGE_PRESETS = [
  { value: 30,  label: "30 days" },
  { value: 60,  label: "60 days" },
  { value: 90,  label: "90 days" },
  { value: 180, label: "180 days" },
  { value: 0,   label: "Any time" },
];
const DEFAULT_DAYS = 60;

// the legacy 60-day, no-filter default — applied on first load so nothing changes until the user filters
const DEFAULT_FILTERS = {
  days: DEFAULT_DAYS,
  postTypes: [],
  contentTypes: [],
  includePlatforms: [],
  excludePlatforms: [],
};

// builds the query string for GET social/post/recycle, matching the backend contract:
//   ?days=30&postTypes[]=TextImage&contentTypes[]=educational&platforms[]=linkedin&excludePlatforms[]=youtube
const buildRecycleQuery = ({ days, postTypes, contentTypes, includePlatforms, excludePlatforms }) => {
  const params = new URLSearchParams();
  // only send days when it differs from the legacy 60-day default
  if (days !== DEFAULT_DAYS) params.append("days", String(days));
  postTypes.forEach((pt) => params.append("postTypes[]", pt));
  contentTypes.forEach((ct) => params.append("contentTypes[]", ct));
  includePlatforms.forEach((p) => params.append("platforms[]", p));
  excludePlatforms.forEach((p) => params.append("excludePlatforms[]", p));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

// number of active (non-default) filters, for the heading badge
const countActiveFilters = (f) =>
  (f.days !== DEFAULT_DAYS ? 1 : 0) +
  f.postTypes.length + f.contentTypes.length +
  f.includePlatforms.length + f.excludePlatforms.length;

const PlatformMultiSelect = ({ formik, allPlatforms }) => {
  const { t } = useTranslation();
  const platforms = allPlatforms ? ALL_PLATFORMS : ALL_PLATFORMS.filter((p) => p.key === "linkedin");
  const selected = formik.values.platforms ?? [];
  const toggle = (key) => formik.setFieldValue(
    "platforms",
    selected.includes(key) ? selected.filter((p) => p !== key) : [...selected, key]
  );
  return (
    <div>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 6, display: "block" }}>
        {t("Backend.Post.Schedule.Platformname")}
      </label>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {platforms.map(({ key, label, icon, color }) => {
          const isSelected = selected.includes(key);
          return (
            <button key={key} type="button" onClick={() => toggle(key)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center",
                gap: 8, padding: "10px 16px", borderRadius: 10, cursor: "pointer",
                border: isSelected ? `2px solid ${color}` : "2px solid #e0e4ea",
                background: isSelected ? `${color}18` : "#fff",
                color: isSelected ? color : "#9299a8",
                transition: "all 0.15s ease", minWidth: 80,
              }}
            >
              {key === "youtube"
                ? <YoutubeIcon height={22} />
                : <FontAwesomeIcon icon={icon} style={{ fontSize: 22 }} />}
              <span style={{ fontSize: 11, fontWeight: 600 }}>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ── filter bar ─────────────────────────────────────────────────────────────────

const FilterChip = ({ label, active, onClick, color = "#038edc", icon }) => (
  <button type="button" onClick={onClick}
    style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "5px 12px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600,
      border: active ? `1.5px solid ${color}` : "1.5px solid #e0e4ea",
      background: active ? `${color}18` : "#fff",
      color: active ? color : "#74788d",
      transition: "all 0.15s ease",
    }}
  >
    {icon}
    {label}
  </button>
);

const FilterSection = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={{ fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 8, display: "block" }}>
      {label}
    </label>
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>{children}</div>
  </div>
);

const PlatformFilterChips = ({ selected, onToggle, allPlatforms, color }) => {
  const platforms = allPlatforms ? ALL_PLATFORMS : ALL_PLATFORMS.filter((p) => p.key === "linkedin");
  return platforms.map(({ key, label, icon }) => (
    <FilterChip key={key} label={label} active={selected.includes(key)} color={color}
      onClick={() => onToggle(key)}
      icon={key === "youtube"
        ? <YoutubeIcon height={14} />
        : <FontAwesomeIcon icon={icon} style={{ fontSize: 13 }} />}
    />
  ));
};

const RecyclerFilterModal = ({ show, setShow, allPlatforms, draft, setDraft, onApply, onReset }) => {
  // toggles a value inside one of the draft's array fields
  const toggleField = (field) => (key) =>
    setDraft((prev) => ({
      ...prev,
      [field]: prev[field].includes(key)
        ? prev[field].filter((x) => x !== key)
        : [...prev[field], key],
    }));

  const ApplyButton = () => (
    <button type="button" className="btn btn-primary" onClick={onApply}>
      Apply filters
    </button>
  );

  const dirty = countActiveFilters(draft) > 0;

  return (
    <NewModal
      show={show}
      setShow={setShow}
      title="Filter recyclable posts"
      ExtraButton={ApplyButton}
      size="lg"
    >
      {dirty && (
        <div className="d-flex justify-content-end mb-2">
          <button type="button" onClick={onReset}
            style={{ border: "none", background: "none", color: "#038edc", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0 }}>
            Reset all
          </button>
        </div>
      )}

      <FilterSection label="Last published">
        {AGE_PRESETS.map(({ value, label }) => (
          <FilterChip key={value} label={label} active={draft.days === value}
            onClick={() => setDraft((prev) => ({ ...prev, days: value }))} />
        ))}
      </FilterSection>

      <FilterSection label="Post type (format)">
        {POST_TYPES.map((pt) => (
          <FilterChip key={pt} label={pt} active={draft.postTypes.includes(pt)}
            onClick={() => toggleField("postTypes")(pt)} />
        ))}
      </FilterSection>

      <FilterSection label="Content type (angle)">
        {CONTENT_TYPES.map((ct) => (
          <FilterChip key={ct} label={ct} active={draft.contentTypes.includes(ct)}
            onClick={() => toggleField("contentTypes")(ct)} />
        ))}
      </FilterSection>

      <FilterSection label="Published on">
        <PlatformFilterChips selected={draft.includePlatforms} onToggle={toggleField("includePlatforms")} allPlatforms={allPlatforms} color="#2e9e65" />
      </FilterSection>

      <div style={{ marginBottom: 0 }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#495057", marginBottom: 8, display: "block" }}>
          Not yet on
        </label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <PlatformFilterChips selected={draft.excludePlatforms} onToggle={toggleField("excludePlatforms")} allPlatforms={allPlatforms} color="#d9534f" />
        </div>
      </div>
    </NewModal>
  );
};

// ── status badge styles (same as Slider.jsx) ─────────────────────────────────

const STATUS_STYLES = {
  draft:       { bg: "#f5f6f8",              color: "#74788d" },
  in_progress: { bg: "rgba(3,142,220,0.12)", color: "#038edc" },
  ready:       { bg: "rgba(81,210,140,0.12)", color: "#2e9e65" },
  published:   { bg: "rgba(81,210,140,0.15)", color: "#1a7a4a" },
  planned:     { bg: "rgba(86,74,177,0.12)", color: "#564ab1" },
};

// ── card ──────────────────────────────────────────────────────────────────────

const RecyclerCard = ({ post, onEdit, onClone }) => {
  const { t } = useTranslation();
  const statusStyle = STATUS_STYLES[post.status] ?? STATUS_STYLES.draft;

  return (
    <div
      onClick={onEdit}
      style={{
        background: "#ffffff",
        border: "1px solid #eef0f3",
        borderTop: "3px solid #038edc",
        borderRadius: 12,
        padding: "14px 16px",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 8,
        cursor: "pointer",
        transition: "box-shadow 0.15s ease, border-color 0.15s ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "none"; }}
    >
      {/* Title + menu */}
      <div className="d-flex align-items-start justify-content-between gap-2">
        <h5 style={{ fontSize: 15, fontWeight: 400, color: "#1a2236", margin: 0, lineHeight: 1.35, flex: 1,
          overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
          {post.title}
        </h5>
        <div onClick={(e) => e.stopPropagation()} style={{ flexShrink: 0 }}>
          <Dropdown style={{ display: "inline-block" }}>
            <EllipsisVerticalToggle />
            <Dropdown.Menu popperConfig={{ strategy: "fixed" }} renderOnMount>
              <Dropdown.Item className="d-flex align-items-center" onClick={onClone}>
                <span style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 24, height: 24, borderRadius: 6, marginRight: 9, flexShrink: 0,
                  background: "rgba(3,142,220,0.12)",
                }}>
                  <FontAwesomeIcon icon={faClone} style={{ fontSize: 11, color: "#038edc" }} />
                </span>
                Repost and clone
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      {/* Date */}
      <p style={{ fontSize: 11, color: "#adb5bd", margin: 0 }}>
        {formatDate(post.last_edit)}
      </p>

      {/* Text preview */}
      <p className="truncate-two-lines" style={{ fontSize: 12, color: "#74788d", margin: 0, flex: 1 }}>
        {post.text}
      </p>

      {/* Interactions */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 4 }}>
        <Interactions
          views={post.stats?.at(-1)?.views ?? 0}
          likes={post.stats?.at(-1)?.likes ?? 0}
          comments={post.stats?.at(-1)?.comments ?? 0}
        />
      </div>

      {/* Badges */}
      <div className="d-flex flex-wrap gap-1" style={{ marginTop: 4 }}>
        {post.content_type && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
            background: "rgba(3,142,220,0.1)", color: "#038edc", letterSpacing: "0.3px" }}>
            {post.content_type}
          </span>
        )}
        {post.post_type && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
            background: "rgba(95,208,243,0.12)", color: "#0e9bbf", letterSpacing: "0.3px" }}>
            {post.post_type}
          </span>
        )}
        {post.status && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 20,
            background: statusStyle.bg, color: statusStyle.color, letterSpacing: "0.3px" }}>
            {post.status}
          </span>
        )}
      </div>
    </div>
  );
};

// ── main component ────────────────────────────────────────────────────────────

const Recycler = () => {
  const { t } = useTranslation();
  const { settings } = useContext(SettingsContext);
  const allPlatforms = canAccessAllPlatforms(settings?.user?.roles);

  const [schedules, setSchedules] = useState([]);
  const [otherTypes, setOtherTypes] = useState([]);
  const [postsData, setPostsData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showEdit, setShowEdit] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [trigger, setTrigger] = useState(-1);

  const [showCloneModal, setShowCloneModal] = useState(false);
  const [clonePostId, setClonePostId] = useState(null);
  const [calendarSchedules, setCalendarSchedules] = useState([]);
  const [calendarDate, setCalendarDate] = useState(new Date());

  // ── recycle filters ──
  // `filters` is what's actually applied (drives the fetch); `draft` is what the modal edits.
  // Defaults to the legacy 60-day / no-filter behaviour, so the first load is unchanged.
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [draftFilters, setDraftFilters] = useState(DEFAULT_FILTERS);
  const [showFilterModal, setShowFilterModal] = useState(false);

  const activeFilterCount = countActiveFilters(filters);
  const filtersActive = activeFilterCount > 0;

  const openFilterModal = () => {
    setDraftFilters(filters); // start from the currently applied filters
    setShowFilterModal(true);
  };

  const applyFilters = () => {
    setFilters(draftFilters);
    setShowFilterModal(false);
  };

  const load = async () => {
    setLoading(true);
    const query = buildRecycleQuery(filters);
    const response = await crudApi.justGet(SocialEndpoints.posts.unplanned + query);
    if (response.ok) {
      setSchedules(response.data.schedules ?? []);
      setOtherTypes(response.data.otherTypes ?? []);
      setPostsData(response.data.posts ?? []);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [filters]);

  const openPost = (post) => {
    setEditPost(post);
    setTrigger((v) => v + 1);
    setShowEdit(true);
  };

  const cloneFormik = useFormik({
    initialValues: { localUserTime: "", timezone: "Europe/Berlin", platforms: ["linkedin"] },
    onSubmit: async (values) => {
      const response = await crudApi.createNew(
        SocialEndpoints.posts.clone + clonePostId,
        values,
        null,
        true
      );
      if (response.ok && response.data?.id) {
        setShowCloneModal(false);
        cloneFormik.resetForm();
        setSchedules((prev) => prev.filter((s) => (s.post ?? s)?.id !== clonePostId));
        setOtherTypes((prev) => prev.filter((p) => p?.id !== clonePostId));
        setPostsData((prev) => prev.filter((p) => p?.id !== clonePostId));
        openPost(response.data);
      }
    },
  });

  const fetchCalendarSchedules = async (date) => {
    const month = moment(date).format("MMYYYY");
    await crudApi.getAll(
      SocialEndpoints.posts.schedule.index + month,
      setCalendarSchedules,
      SocialEndpoints.posts.scheduledDataField
    );
  };

  const openCloneModal = (postId) => {
    setClonePostId(postId);
    cloneFormik.resetForm();
    fetchCalendarSchedules(calendarDate);
    setShowCloneModal(true);
  };

  const seen = new Set();
  const posts = [
    ...schedules.map((s) => s.post ?? s),
    ...otherTypes,
    ...postsData,
  ].filter((p) => {
    if (!p?.id || seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });

  const SaveClone = () => <SaveButton formik={cloneFormik} />;

  return (
    <div className="flex-fill">
      <PostFormView
        showEdit={showEdit}
        setShowEdit={setShowEdit}
        editData={editPost}
        trigger={trigger}
        setTrigger={setTrigger}
        refreshOnClose={true}
        onClose={load}
      />

      <NewModal
        show={showCloneModal}
        setShow={setShowCloneModal}
        title="Repost and clone"
        ExtraButton={SaveClone}
        onExit={() => cloneFormik.resetForm()}
        size="xl"
      >
        <Row>
          <Col>
            <Row>
              <Col>
                <DateTimeField
                  formik={cloneFormik}
                  label={t("Backend.Post.Schedule.ScheduleFor")}
                  valueName="localUserTime"
                />
              </Col>
              <Col>
                <Timezones formik={cloneFormik} />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <PlatformMultiSelect formik={cloneFormik} allPlatforms={allPlatforms} />
              </Col>
            </Row>
            <Row className="mt-4">
              <Col>
                <PostCalendar
                  schedules={calendarSchedules}
                  setSchedules={setCalendarSchedules}
                  dontUseCalendarClick={true}
                  date={calendarDate}
                  onMonthChange={(newDate) => {
                    const d = moment(newDate).startOf("month").toDate();
                    setCalendarDate(d);
                    fetchCalendarSchedules(d);
                  }}
                  onPlanSaved={() => fetchCalendarSchedules(calendarDate)}
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </NewModal>

      <RecyclerFilterModal
        show={showFilterModal}
        setShow={setShowFilterModal}
        allPlatforms={allPlatforms}
        draft={draftFilters}
        setDraft={setDraftFilters}
        onApply={applyFilters}
        onReset={() => setDraftFilters(DEFAULT_FILTERS)}
      />

      <div className="d-flex align-items-center justify-content-between mb-4">
        <h4 style={{ display: "flex", alignItems: "center", gap: 10, margin: 0 }}>
          <FontAwesomeIcon icon={faRecycle} style={{ color: "#038edc", fontSize: 18 }} />
          Recycling
          {!loading && posts.length > 0 && (
            <span style={{
              fontSize: 12, fontWeight: 700, padding: "2px 9px", borderRadius: 20,
              background: "rgba(3,142,220,0.12)", color: "#038edc",
            }}>
              {posts.length}
            </span>
          )}
        </h4>

        <button type="button" onClick={openFilterModal}
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            padding: "7px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600,
            border: filtersActive ? "1.5px solid #038edc" : "1.5px solid #e0e4ea",
            background: filtersActive ? "rgba(3,142,220,0.08)" : "#fff",
            color: filtersActive ? "#038edc" : "#495057",
            transition: "all 0.15s ease",
          }}
        >
          <FontAwesomeIcon icon={faFilter} style={{ fontSize: 12 }} />
          Filters
          {filtersActive && (
            <span style={{
              fontSize: 11, fontWeight: 700, minWidth: 18, height: 18, padding: "0 5px", borderRadius: 20,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              background: "#038edc", color: "#fff",
            }}>
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      {loading ? (
        <p style={{ color: "#6b7280", fontSize: 13 }}>Loading…</p>
      ) : posts.length === 0 ? (
        <Card>
          <CardBody style={{ textAlign: "center", padding: 48, color: "#6b7280" }}>
            <FontAwesomeIcon icon={faRecycle} style={{ fontSize: 44, marginBottom: 16, opacity: 0.25 }} />
            {filtersActive ? (
              <>
                <p style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                  No posts match your filters
                </p>
                <p style={{ fontSize: 13, maxWidth: 420, margin: "0 auto", lineHeight: 1.6 }}>
                  Try widening the time window or removing a platform / post-type filter.
                </p>
              </>
            ) : (
              <>
                <p style={{ fontSize: 15, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                  You're all caught up!
                </p>
                <p style={{ fontSize: 13, maxWidth: 420, margin: "0 auto", lineHeight: 1.6 }}>
                  Not every post needs to be written from scratch. Posts that are older than 60 days
                  can be recycled — updated and scheduled again to reach new audiences.
                  Check back once your older content is eligible.
                </p>
              </>
            )}
          </CardBody>
        </Card>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          gridAutoRows: "280px",
          gap: 16,
        }}>
          {posts.map((post) => (
            <RecyclerCard
              key={post.id}
              post={post}
              onEdit={() => openPost(post)}
              onClone={() => openCloneModal(post.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Recycler;
